/**
 * Tiny module-scoped router-state singleton.
 *
 * Single source of truth for the current pathname so every component
 * (nav-bar, deck-card, future widgets) reads from one place.
 *
 * Why a module singleton instead of per-component state?
 *
 *   Before: nav-bar tracked `path` as a Lit @state property and
 *   registered a `window.addEventListener('click', this.onClick)` in
 *   connectedCallback. The arrow-function class field captured `this`
 *   = the original instance. When Vite HMR swapped the module, the
 *   window listener still pointed at the now-orphan instance — clicks
 *   updated the orphan's `this.path`, the in-DOM nav-bar never
 *   re-rendered, and the highlight stayed wrong until a full reload.
 *
 *   After: the navigation listener is registered ONCE here at module
 *   load (not per-instance). Subscribers are tracked in a Set;
 *   connectedCallback adds, disconnectedCallback removes. HMR can
 *   swap the module body, but the singleton state survives. Every
 *   live `nav-bar` (or `deck-card`, etc.) subscribes and gets the
 *   notification.
 *
 *   `navigate(href)` is the only sanctioned way to change the URL
 *   from inside the app — it pushState's once and notifies. Browser
 *   back/forward still works because we also listen for the native
 *   `popstate` and forward to subscribers.
 */

type Subscriber = (path: string) => void;

const subscribers = new Set<Subscriber>();

function notify(path: string): void {
  // Snapshot to a copy so a subscriber unsubscribing during the
  // callback (rare but possible) doesn't mutate-during-iteration.
  for (const cb of [...subscribers]) {
    try {
      cb(path);
    } catch (err) {
      console.error('[router-state] subscriber error:', err);
    }
  }
}

// Bind the popstate listener ONCE at module load (not lazily on first
// subscribe) so a `navigate()` that fires before any component has
// subscribed still drives the notify path. Browser back/forward and our
// own synthetic popstate (from navigate, below) both funnel through here.
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => notify(window.location.pathname));
}

/** Read the current pathname. */
export function currentPath(): string {
  return window.location.pathname;
}

/**
 * Navigate to a new pathname without a full document load.
 *
 * Calls `history.pushState` and then dispatches a synthetic `popstate`.
 * The popstate is load-bearing, not cosmetic: @lit-labs/router only
 * re-resolves its outlet on `popstate` (its click handler bails when the
 * event is `defaultPrevented`, which nav-bar always does), so a bare
 * `pushState` would change the URL but leave the outlet on the previous
 * route. Dispatching `popstate` makes BOTH the router (via its own
 * `_onPopState` -> `goto`) and our subscribers (via the module-level
 * listener above -> `notify`) re-sync from one authoritative signal —
 * for in-app link clicks AND programmatic redirects alike.
 *
 * No-op when `href` already matches the current location.
 */
export function navigate(href: string): void {
  if (href === window.location.pathname) return;
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Subscribe to pathname changes. Returns an unsubscribe function.
 * Idempotent: subscribing the same callback twice is a no-op.
 */
export function subscribe(cb: Subscriber): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}
